import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { useEnumLabel, useInstitutionTypeLabel } from '@/components/platform/useLabel';
import { platformApi } from '@/api/platform';
import { useAsyncData } from '@/hooks';
import { institutionDisplayName, type PlatformInstitutionRecord } from '@/types/platform';
import { formatDate } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * The register of every institution on the platform.
 *
 * THIS ENDPOINT DOES NOT EXIST AND NOTHING HERE PRETENDS OTHERWISE. Core has a
 * `platform_institutions` table and no route over it: every other institution
 * endpoint — structure, lifecycle, products, feature flags, integrations — takes
 * an institution id and none of them will hand one out. The call is left in place
 * so this screen reports a 404 rather than an empty directory, and the notice
 * below says which route is missing so whoever picks it up knows what to build.
 *
 * NO CLIENT-SIDE SUBSTITUTE. The dashboard's attention list and the onboarding
 * pipeline both contain institution ids, and folding them together here would put
 * a partial list under a heading that says "all institutions". An operator who
 * searched that list and found nothing would conclude the institution does not
 * exist, which is a worse outcome than a screen that admits it cannot answer.
 *
 * The columns are what `platform_institutions` actually holds. There is no
 * `enabledProductCount` or `incompleteProductCount` on that table — those were
 * invented here, and they would have rendered as zero next to a school with six
 * live products the moment the route appeared.
 */
export function InstitutionsPage() {
  const { t, i18n } = useTranslation();
  const typeLabel = useInstitutionTypeLabel();
  const label = useEnumLabel();
  const locale = i18n.language;

  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');

  const fetcher = useCallback(() => platformApi.institutions.list(search || undefined), [search]);
  const { state, reload } = useAsyncData<PlatformInstitutionRecord[]>(`institutions:${search}`, fetcher);

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{t('platform.institutions.title')}</h1>
          <p className={styles.pageLead}>{t('platform.institutions.lead')}</p>
        </div>
        <Link className={styles.primaryBtn} to="/admin/onboarding">
          <Icon name="plus" size={16} />
          {t('platform.institutions.create')}
        </Link>
      </header>

      {/*
        Named explicitly rather than left to the generic "not deployed" notice, so
        the gap is a work item rather than a mystery. Every other route this
        console needs for an institution is live once its id is known.
      */}
      <p className={styles.dataNotice}>
        <Icon name="info" size={15} aria-hidden="true" />
        {t('platform.institutions.registryMissing')}
      </p>

      <form
        className={styles.filterBar}
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          setSearch(input.trim());
        }}
      >
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="institution-search">
            {t('platform.institutions.searchLabel')}
          </label>
          <input
            id="institution-search"
            className={styles.input}
            type="search"
            value={input}
            placeholder={t('platform.institutions.searchPlaceholder')}
            onChange={(event) => setInput(event.target.value)}
          />
        </div>
        <button type="submit" className={styles.secondaryBtn}>
          <Icon name="search" size={15} />
          {t('common.search')}
        </button>
        <button type="button" className={styles.secondaryBtn} onClick={reload}>
          <Icon name="refresh" size={15} />
          {t('platform.dashboard.refresh')}
        </button>
      </form>

      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t('platform.institutions.emptyTitle')}
        emptyBody={t('platform.institutions.emptyBody')}
      >
        {(data) => (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <caption className={styles.tableCaption}>
                {t('platform.institutions.tableCaption', { n: data.length })}
              </caption>
              <thead>
                <tr>
                  <th scope="col">{t('platform.institutions.colName')}</th>
                  <th scope="col">{t('platform.institutions.colType')}</th>
                  <th scope="col">{t('platform.institutions.colStatus')}</th>
                  <th scope="col">{t('platform.institutions.colCountry')}</th>
                  <th scope="col">{t('platform.institutions.created')}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((institution) => (
                  <tr key={institution.id}>
                    <th scope="row" className={styles.tableRowHeader}>
                      <Link
                        className={styles.rowTitleLink}
                        to={`/admin/institutions/${encodeURIComponent(institution.id)}`}
                      >
                        {institutionDisplayName(institution)}
                      </Link>
                      <span className={styles.tableSub}>{institution.code}</span>
                    </th>
                    <td>{typeLabel(institution.institutionType)}</td>
                    <td>
                      <span
                        className={
                          institution.onboardingState === 'ACTIVE'
                            ? styles.okTag
                            : institution.onboardingState === 'SUSPENDED'
                              ? styles.dangerTag
                              : styles.mutedTag
                        }
                      >
                        {label('state.onboarding', institution.onboardingState)}
                      </span>
                    </td>
                    <td className={styles.mutedCell}>{institution.countryCode ?? '—'}</td>
                    <td className={styles.mutedCell}>{formatDate(institution.createdAt, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncPanel>
    </div>
  );
}
